import { ORDER_TYPE } from '@exam-management/common/utils';
import { FilterOperator, useSearchQConfigsQuery } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useNavigate, useParams } from '@exam-management/router';
import { ErrorBox, type GridCellParams } from '@shared/components';
import { useHasPermission } from '@shared/permissions';

import { useQConfigSearchParams } from '../../hooks';
import { QConfigListTable } from '../qconfig-list-table';

export default function QConfigList() {
  const navigate = useNavigate();
  const { courseId } = useParams('/exam-courses/:courseId/qconfigs');
  const hasQConfigCreatePermission = useHasPermission(APP_PERMISSIONS.EXAM.QCONFIG.CREATE);

  const [{ page, pageSize, field, sort }, setQConfigSearchParams] = useQConfigSearchParams();

  const {
    data,
    loading: isLoading,
    error,
  } = useSearchQConfigsQuery({
    variables: {
      searchQConfigsInput: {
        filter: {
          conditions: [
            {
              field: 'examCourseId',
              operator: FilterOperator.Equal,
              value: courseId,
            },
          ],
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        order: {
          field,
          type: ORDER_TYPE[sort],
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const qConfigCount = data?.excSearchQConfigs.total ?? 0;

  const rows = (data?.excSearchQConfigs.qConfigs ?? []).map((qConfig) => ({
    id: qConfig.id,
    code: qConfig.code,
    name: qConfig.name,
    type: qConfig.type,
    status: qConfig.status,
    updatedAt: qConfig.updatedAt,
  }));

  if (error) {
    return <ErrorBox />;
  }

  const navigateToQConfig = (cell: GridCellParams) => {
    if (cell.field !== 'moreAction')
      navigate(`/exam-courses/:courseId/qconfigs/:qconfigId`, {
        params: { courseId: courseId, qconfigId: cell.id.toString() },
      });
  };

  return (
    <QConfigListTable
      rows={rows}
      loading={isLoading}
      hideFooter={qConfigCount === 0}
      onCellClick={navigateToQConfig}
      columnVisibilityModel={{ moreAction: hasQConfigCreatePermission }}
      slotProps={{
        footer: {
          total: qConfigCount,
          pagination: {
            page: page,
            count: qConfigCount,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              setQConfigSearchParams({
                page: page,
              });
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);

              setQConfigSearchParams({
                pageSize: pageSize,
                page: 0,
              });
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const field = sorting[0]?.field ?? 'updatedAt';
        const sort = sorting[0]?.sort ?? 'desc';
        setQConfigSearchParams({
          field: field,
          sort: sort,
        });
      }}
      initialState={{
        sorting: {
          sortModel: [{ field, sort }],
        },
      }}
    />
  );
}
